import './StatsChart.sass';

import { FC, useEffect, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import format from 'date-fns/format';
import { fromUnixTime } from 'date-fns';

import { CurrencyEnum } from '$store/models';
import { useTranslator } from '$hooks';

import type { EarningsCategoryMeta, IncomeType } from '../StatisticsPro.types';

export interface StatsChartProps {
  days: number[];
  values: Record<number, Partial<Record<IncomeType, number>>>;
  categories: EarningsCategoryMeta[];
  selected: IncomeType[];
  labels: Record<string, string>;
  currency: CurrencyEnum;
}

const amountFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const OTHERS_COLOR = '#9b9ba0';

const ALL_COLOR = '#3478f6';

export const StatsChart: FC<StatsChartProps> = ({
  days,
  values,
  categories,
  selected,
  labels,
  currency,
}) => {
  const t = useTranslator();
  const daysKey = days.length
    ? `${days[0]}_${days[days.length - 1]}_${days.length}`
    : 'empty';
  const prevDaysKeyRef = useRef<string | null>(null);
  const shouldAnimate = prevDaysKeyRef.current !== daysKey;
  useEffect(() => {
    prevDaysKeyRef.current = daysKey;
  }, [daysKey]);
  const total = useMemo(() => {
    let sum = 0;
    for (const d of days) {
      const dayValues = values[d];
      if (!dayValues) continue;
      for (const v of Object.values(dayValues)) {
        sum += v ?? 0;
      }
    }
    return sum;
  }, [days, values]);

  const options: Highcharts.Options = useMemo(() => {
    const xCategories = days.map((d) => format(fromUnixTime(d), 'd MMM'));

    const colorOf = (type: IncomeType) =>
      categories.find((c) => c.type === type)?.color ?? OTHERS_COLOR;

    const series: Highcharts.SeriesOptionsType[] = [];

    for (const cat of selected) {
      const data = days.map((d) => values[d]?.[cat] ?? 0);
      series.push({
        type: 'spline',
        name: labels[cat] ?? cat,
        data,
        color: colorOf(cat),
      });
    }

    const hiddenOthers = categories
      .filter((meta) => !selected.includes(meta.type))
      .map((meta) => meta.type);

    if (hiddenOthers.length > 0) {
      const data = days.map((d) => {
        let sum = 0;
        for (const cat of hiddenOthers) {
          sum += values[d]?.[cat] ?? 0;
        }
        return sum;
      });
      const isAllAggregate = selected.length === 0;
      series.push({
        type: 'spline',
        name: isAllAggregate ? t('statistics_pro_all') : t('statistics_pro_others'),
        data,
        color: isAllAggregate ? ALL_COLOR : OTHERS_COLOR,
        dashStyle: isAllAggregate ? 'Solid' : 'Dash',
        lineWidth: isAllAggregate ? 2 : 1,
      });
    }

    return {
      title: { text: '' },
      chart: {
        type: 'spline',
        backgroundColor: 'transparent',
        height: 320,
        spacing: [8, 8, 8, 8],
        style: { fontFamily: "'Nunito Sans', sans-serif" },
      },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories: xCategories,
        labels: { style: { color: '#9b9ba0' } },
        lineColor: 'rgba(255,255,255,0.06)',
        tickColor: 'rgba(255,255,255,0.06)',
        tickInterval: Math.max(1, Math.floor(xCategories.length / 8)),
      },
      yAxis: {
        title: { text: '' },
        labels: { enabled: false },
        gridLineColor: 'rgba(255,255,255,0.05)',
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: '#1c1c1f',
        borderColor: 'rgba(255,255,255,0.1)',
        style: { color: '#fff' },
        formatter() {
          const point = this as Highcharts.TooltipFormatterContextObject;
          const rows = (point.points ?? [])
            .map(
              (p) =>
                `<div style="display:flex;justify-content:space-between;gap:12px;">
                   <span><span style="color:${p.color};">●</span> ${p.series.name}</span>
                   <b>${Number(p.y ?? 0).toFixed(2)} ${currency.toUpperCase()}</b>
                 </div>`,
            )
            .join('');
          const total = (point.points ?? []).reduce(
            (acc, p) => acc + Number(p.y ?? 0),
            0,
          );
          return `<div style="font-size:12px;min-width:180px;">
            <div style="margin-bottom:6px;opacity:0.7;">${point.x}</div>
            ${rows}
            <div style="margin-top:6px;border-top:1px solid rgba(255,255,255,0.08);padding-top:4px;display:flex;justify-content:space-between;">
              <span>${t('statistics_pro_total')}</span><b>${total.toFixed(
            2,
          )} ${currency.toUpperCase()}</b>
            </div>
          </div>`;
        },
      },
      plotOptions: {
        series: {
          animation: shouldAnimate ? { duration: 900 } : false,
          marker: { enabled: false, symbol: 'circle' },
          states: { inactive: { opacity: 0.4 } },
        },
      },
      series,
    };
  }, [days, values, categories, selected, labels, currency, t, shouldAnimate]);

  const isEmpty = ((options.series as Highcharts.SeriesOptionsType[]) ?? []).length === 0;

  return (
    <div className="StatsChart">
      <div className="StatsChart__plot">
        <div className="StatsChart__header">
          <div className="StatsChart__title">{t('statistics_pro_period_total')}</div>
          <div className="StatsChart__title">
            {amountFormatter.format(total)} {currency.toUpperCase()}
          </div>
        </div>
        {isEmpty ? (
          <div className="StatsChart__empty">{t('statistics_pro_no_data')}</div>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            allowChartUpdate
            immutable
          />
        )}
      </div>
    </div>
  );
};
